import type { IDBPDatabase, IDBPTransaction } from 'idb';
import {
  validId, validRevision, validExercise, validExerciseRevision, validRoutine,
  validRoutineRevision, editExercise, editRoutine, archiveExercise, archiveRoutine,
  trainingFailure as failure,
} from '../domain/training.ts';
import type { Exercise, ExerciseRevision, Routine, RoutineRevision, TrainingResult } from '../domain/training.ts';
import type {
  TrainingRepository, TrainingMutation, TrainingContext, TrainingCommitReceipt,
  RevisionedTrainingCommitReceipt, CreateExerciseCommand, EditExerciseRepositoryCommand,
  ArchiveExerciseCommand, CreateRoutineCommand, EditRoutineRepositoryCommand, ArchiveRoutineCommand,
} from '../ports/training-repository.ts';
import { openFenixDatabase, storageFailure, validDatasetControl } from './fenix-db.ts';
import type { FenixSchema } from './fenix-db.ts';
import { canonical } from './canonical.ts';
import { validMutation, validTrainingReceipt as validReceipt } from './training-receipts.ts';
import type { StoredTrainingReceipt as StoredReceipt } from './training-receipts.ts';

const stores = ['control', 'training_exercises', 'training_exercise_revisions',
  'training_routines', 'training_routine_revisions', 'training_receipts'] as const;
type Tx = IDBPTransaction<FenixSchema, typeof stores, 'readwrite' | 'readonly'>;
type Entity = Exercise | Routine;
type Revision = ExerciseRevision | RoutineRevision;
type Action = 'create' | 'edit' | 'archive';
type MutationResult = { status: 'APPLIED' | 'REPLAYED'; receipt: TrainingCommitReceipt & { revision?: number } };
type CatalogPort = Pick<TrainingRepository,
  'readContext' |
  'listExercises' | 'readExercise' | 'readExerciseRevision' | 'createExercise' | 'editExercise' | 'archiveExercise' |
  'listRoutines' | 'readRoutine' | 'readRoutineRevision' | 'createRoutine' | 'editRoutine' | 'archiveRoutine'>;
type Pair<E, R> = { entity: E; revision: R };
interface Spec<E extends Entity, R extends Revision> {
  kind: 'exercise' | 'routine';
  current: 'training_exercises' | 'training_routines';
  history: 'training_exercise_revisions' | 'training_routine_revisions';
  validEntity(value: unknown): value is E;
  validRevision(value: unknown): value is R;
  id(entity: E): string;
  owner(revision: R): string;
  ref(revision: R): string;
  edit(entity: E, current: R, next: R, expected: number): TrainingResult<Pair<E, R>>;
  archive(entity: E): TrainingResult<E>;
}
const exercise: Spec<Exercise, ExerciseRevision> = {
  kind: 'exercise', current: 'training_exercises', history: 'training_exercise_revisions',
  validEntity: (v): v is Exercise => validExercise(v as Exercise),
  validRevision: (v): v is ExerciseRevision => validExerciseRevision(v as ExerciseRevision),
  id: v => v.exerciseId, owner: v => v.exerciseId, ref: v => v.exerciseRevisionId,
  edit(entity, current, next, expectedRevision) {
    const result = editExercise(entity, current, { ...next, expectedRevision });
    return result.ok ? { ok: true, value: { entity: result.value.exercise, revision: result.value.revision } } : result;
  },
  archive: archiveExercise,
};
const routine: Spec<Routine, RoutineRevision> = {
  kind: 'routine', current: 'training_routines', history: 'training_routine_revisions',
  validEntity: (v): v is Routine => validRoutine(v as Routine),
  validRevision: (v): v is RoutineRevision => validRoutineRevision(v as RoutineRevision),
  id: v => v.routineId, owner: v => v.routineId, ref: v => v.routineRevisionId,
  edit(entity, current, next, expectedRevision) {
    const result = editRoutine(entity, current, { ...next, expectedRevision });
    return result.ok ? { ok: true, value: { entity: result.value.routine, revision: result.value.revision } } : result;
  },
  archive: archiveRoutine,
};

export type TrainingFault = 'after-current' | 'after-revision' | 'after-receipt' | 'after-control' | 'lost-ack';
export class TrainingLabFaults {
  next: TrainingFault | null = null;
  take() { const fault = this.next; this.next = null; return fault; }
}
function inject(fault: TrainingFault | null, point: TrainingFault) {
  if (fault === point) throw new DOMException('Synthetic Training transaction abort', 'AbortError');
}

export class IndexedTrainingCatalog implements CatalogPort {
  private available = true;
  constructor(private readonly db: IDBPDatabase<FenixSchema>, private readonly faults = new TrainingLabFaults()) {
    db.addEventListener('versionchange', () => this.close());
    db.addEventListener('close', () => { this.available = false; });
  }
  close() { this.available = false; this.db.close(); }

  readContext(): Promise<TrainingResult<TrainingContext>> {
    return this.transaction('readonly', async tx => {
      const result = await this.control(tx);
      return result.ok ? { ok: true, value: {
        datasetId: result.value.activeDatasetId, controlEpoch: result.value.controlEpoch,
      } } : result;
    });
  }

  private async transaction<M extends 'readonly' | 'readwrite', T>(mode: M, run: (tx: IDBPTransaction<FenixSchema, typeof stores, M>) => Promise<TrainingResult<T>>): Promise<TrainingResult<T>> {
    if (!this.available) return failure('STORAGE_UNAVAILABLE');
    let tx: IDBPTransaction<FenixSchema, typeof stores, M> | undefined;
    try {
      tx = this.db.transaction(stores, mode, { durability: 'strict' });
      void tx.done.catch(() => {});
      const result = await run(tx);
      if (!result.ok) {
        tx.abort();
        await tx.done.catch(() => {});
        return result;
      }
      await tx.done;
      return result;
    } catch (error) {
      if (tx) { try { tx.abort(); } catch { /* already settled */ } await tx.done.catch(() => {}); }
      return storageFailure(error);
    }
  }
  private async control(tx: Tx, context?: TrainingContext) {
    const control = await tx.objectStore('control').get('control');
    if (!validDatasetControl(control)) return failure('CORRUPTION_DETECTED');
    if (control.restoreFence !== null) return failure('RESTORE_IN_PROGRESS');
    if (control.recoveryDatasetId !== null) return failure('CORRUPTION_DETECTED');
    if (context && (control.activeDatasetId !== context.datasetId || control.controlEpoch !== context.controlEpoch))
      return failure('DATASET_STALE');
    return { ok: true as const, value: control };
  }
  private async historical<E extends Entity, R extends Revision>(tx: Tx, spec: Spec<E, R>, datasetId: string, ref: string): Promise<TrainingResult<R>> {
    const stored = await tx.objectStore(spec.history).get([datasetId, ref]);
    if (!stored) return failure('NOT_FOUND');
    if (stored.datasetId !== datasetId || !spec.validRevision(stored.record) || spec.ref(stored.record) !== ref)
      return failure('CORRUPTION_DETECTED');
    return { ok: true, value: stored.record };
  }
  private async pair<E extends Entity, R extends Revision>(tx: Tx, spec: Spec<E, R>, datasetId: string, id: string): Promise<TrainingResult<Pair<E, R>>> {
    const stored = await tx.objectStore(spec.current).get([datasetId, id]);
    if (!stored) return failure('NOT_FOUND');
    if (stored.datasetId !== datasetId || !spec.validEntity(stored.record) || spec.id(stored.record) !== id)
      return failure('CORRUPTION_DETECTED');
    const revision = await this.historical(tx, spec, datasetId, stored.record.currentRevisionRef);
    if (!revision.ok || spec.owner(revision.value) !== id) return failure('CORRUPTION_DETECTED');
    return { ok: true, value: { entity: stored.record, revision: revision.value } };
  }
  private read<E extends Entity, R extends Revision>(spec: Spec<E, R>, id: string) {
    if (!validId(id)) return Promise.resolve(failure('VALIDATION_ERROR'));
    return this.transaction('readonly', async tx => {
      const control = await this.control(tx); if (!control.ok) return control;
      const pair = await this.pair(tx, spec, control.value.activeDatasetId, id); if (!pair.ok) return pair;
      return { ok: true, value: { ...pair.value, context: { datasetId: control.value.activeDatasetId, controlEpoch: control.value.controlEpoch } } };
    });
  }
  private readRevision<E extends Entity, R extends Revision>(spec: Spec<E, R>, ref: string) {
    if (!validId(ref)) return Promise.resolve(failure('VALIDATION_ERROR'));
    return this.transaction('readonly', async tx => {
      const control = await this.control(tx); if (!control.ok) return control;
      return this.historical(tx, spec, control.value.activeDatasetId, ref);
    });
  }
  private list<E extends Entity, R extends Revision>(spec: Spec<E, R>) {
    return this.transaction('readonly', async tx => {
      const control = await this.control(tx); if (!control.ok) return control;
      const datasetId = control.value.activeDatasetId;
      const keys = await tx.objectStore(spec.current).getAllKeys(IDBKeyRange.bound([datasetId], [datasetId, []]));
      const values = [];
      for (const key of keys) {
        const pair = await this.pair(tx, spec, datasetId, key[1]); if (!pair.ok) return pair;
        values.push({ ...pair.value, context: { datasetId, controlEpoch: control.value.controlEpoch } });
      }
      return { ok: true, value: values };
    });
  }

  private async mutate<E extends Entity, R extends Revision>(spec: Spec<E, R>, action: Action,
    command: TrainingMutation, id: string, entity?: E, revision?: R, expected?: number,
  ): Promise<TrainingResult<MutationResult>> {
    if (!validMutation(command) || !validId(id)
      || (action === 'create' && (!spec.validEntity(entity) || !spec.validRevision(revision)))
      || (action !== 'create' && !validRevision(expected))
      || (action === 'edit' && !spec.validRevision(revision))) return failure('VALIDATION_ERROR');
    // Capture inputs before the first await: caller mutation cannot alter an in-flight intent.
    let intent: string;
    try {
      intent = canonical({ kind: spec.kind, action, command });
      command = structuredClone(command);
      entity = entity === undefined ? undefined : structuredClone(entity);
      revision = revision === undefined ? undefined : structuredClone(revision);
    } catch { return failure('VALIDATION_ERROR'); }
    let lostAck = false;
    const result = await this.transaction<'readwrite', MutationResult>('readwrite', async tx => {
      const checked = await this.control(tx, command.context); if (!checked.ok) return checked;
      const control = checked.value;
      const datasetId = control.activeDatasetId;
      const prior = await tx.objectStore('training_receipts').get([datasetId, command.operationId]);
      if (prior) {
        if (prior.datasetId !== datasetId || !validReceipt(prior.record)
          || prior.record.datasetId !== datasetId || prior.record.operationId !== command.operationId)
          return failure('CORRUPTION_DETECTED');
        const receipt = prior.record;
        if (receipt.fingerprint !== intent) return failure('INVARIANT_CONFLICT');
        if (receipt.kind !== spec.kind || receipt.action !== action || receipt.receipt.ownerId !== id)
          return failure('CORRUPTION_DETECTED');
        const historical = await this.historical(tx, spec, datasetId, receipt.revisionRef);
        if (!historical.ok || spec.owner(historical.value) !== id
          || (action !== 'archive' && historical.value.revision !== receipt.receipt.revision)) return failure('CORRUPTION_DETECTED');
        return { ok: true, value: { status: 'REPLAYED' as const, receipt: receipt.receipt } };
      }
      let next: E;
      let snapshot: R;
      if (action === 'create') {
        next = entity!; snapshot = revision!;
        if (spec.id(next) !== id || spec.owner(snapshot) !== id || next.currentRevisionRef !== spec.ref(snapshot)
          || snapshot.revision !== 1) return failure('INVARIANT_CONFLICT');
        if (await tx.objectStore(spec.current).get([datasetId, id])) return failure('INVARIANT_CONFLICT');
      } else {
        const current = await this.pair(tx, spec, datasetId, id); if (!current.ok) return current;
        if (current.value.revision.revision !== expected) return failure('REVISION_CONFLICT');
        if (action === 'archive') {
          const archived = spec.archive(current.value.entity); if (!archived.ok) return archived;
          next = archived.value; snapshot = current.value.revision;
        } else {
          if (spec.owner(revision!) !== id || revision!.revision !== current.value.revision.revision + 1)
            return failure('INVARIANT_CONFLICT');
          const edited = spec.edit(current.value.entity, current.value.revision, revision!, expected!);
          if (!edited.ok) return edited;
          next = edited.value.entity; snapshot = edited.value.revision;
        }
      }
      if (action !== 'archive') {
        if (await tx.objectStore(spec.history).get([datasetId, spec.ref(snapshot)])) return failure('INVARIANT_CONFLICT');
        if ('exercises' in snapshot) {
          for (const ref of snapshot.exercises) {
            const target = await this.historical(tx, exercise, datasetId, ref.exerciseRevisionRef);
            if (!target.ok) return target;
          }
        }
      }
      if (!Number.isSafeInteger(control.datasetRevision + 1)) return failure('INVARIANT_CONFLICT');
      const fault = this.faults.take();
      if (spec.kind === 'exercise' && exercise.validEntity(next))
        await tx.objectStore('training_exercises').put({ datasetId, record: next }, [datasetId, id]);
      else if (spec.kind === 'routine' && routine.validEntity(next))
        await tx.objectStore('training_routines').put({ datasetId, record: next }, [datasetId, id]);
      else return failure('CORRUPTION_DETECTED');
      inject(fault, 'after-current');
      if (action !== 'archive') {
        if (spec.kind === 'exercise' && exercise.validRevision(snapshot))
          await tx.objectStore('training_exercise_revisions').add({ datasetId, record: snapshot }, [datasetId, spec.ref(snapshot)]);
        else if (spec.kind === 'routine' && routine.validRevision(snapshot))
          await tx.objectStore('training_routine_revisions').add({ datasetId, record: snapshot }, [datasetId, spec.ref(snapshot)]);
        else return failure('CORRUPTION_DETECTED');
      }
      inject(fault, 'after-revision');
      const receipt = { operationId: command.operationId, ownerId: id, ...(action === 'archive' ? {} : { revision: snapshot.revision }) };
      const record: StoredReceipt = { operationId: command.operationId, datasetId, kind: spec.kind,
        action, fingerprint: intent, revisionRef: spec.ref(snapshot), receipt };
      await tx.objectStore('training_receipts').add({ datasetId, record }, [datasetId, command.operationId]);
      inject(fault, 'after-receipt');
      await tx.objectStore('control').put({ ...control, datasetRevision: control.datasetRevision + 1 });
      inject(fault, 'after-control');
      lostAck = fault === 'lost-ack';
      return { ok: true, value: { status: 'APPLIED' as const, receipt } };
    });
    return result.ok && lostAck ? failure('STORAGE_UNAVAILABLE') : result;
  }
  private async revisioned(result: ReturnType<IndexedTrainingCatalog['mutate']>) {
    const value = await result;
    if (!value.ok) return value;
    if (!validRevision(value.value.receipt.revision)) return failure('CORRUPTION_DETECTED');
    return { ok: true as const, value: { ...value.value, receipt: value.value.receipt as RevisionedTrainingCommitReceipt } };
  }
  async readExercise(id: string) {
    const r = await this.read(exercise, id);
    return r.ok ? { ok: true as const, value: { exercise: r.value.entity, revision: r.value.revision, context: r.value.context } } : r;
  }
  readExerciseRevision(ref: string) { return this.readRevision(exercise, ref); }
  async listExercises() {
    const r = await this.list(exercise);
    return r.ok ? { ok: true as const, value: r.value.map(v => ({ exercise: v.entity, revision: v.revision, context: v.context })) } : r;
  }
  createExercise(c: CreateExerciseCommand) { return this.revisioned(this.mutate(exercise, 'create', c, c?.exercise?.exerciseId, c?.exercise, c?.revision)); }
  editExercise(c: EditExerciseRepositoryCommand) { return this.revisioned(this.mutate(exercise, 'edit', c, c?.exerciseId, undefined, c?.nextRevision, c?.expectedRevision)); }
  archiveExercise(c: ArchiveExerciseCommand) { return this.mutate(exercise, 'archive', c, c?.exerciseId, undefined, undefined, c?.expectedRevision); }
  async readRoutine(id: string) {
    const r = await this.read(routine, id);
    return r.ok ? { ok: true as const, value: { routine: r.value.entity, revision: r.value.revision, context: r.value.context } } : r;
  }
  readRoutineRevision(ref: string) { return this.readRevision(routine, ref); }
  async listRoutines() {
    const r = await this.list(routine);
    return r.ok ? { ok: true as const, value: r.value.map(v => ({ routine: v.entity, revision: v.revision, context: v.context })) } : r;
  }
  createRoutine(c: CreateRoutineCommand) { return this.revisioned(this.mutate(routine, 'create', c, c?.routine?.routineId, c?.routine, c?.revision)); }
  editRoutine(c: EditRoutineRepositoryCommand) { return this.revisioned(this.mutate(routine, 'edit', c, c?.routineId, undefined, c?.nextRevision, c?.expectedRevision)); }
  archiveRoutine(c: ArchiveRoutineCommand) { return this.mutate(routine, 'archive', c, c?.routineId, undefined, undefined, c?.expectedRevision); }
}

export async function openTrainingCatalog(name: string, faults = new TrainingLabFaults()): Promise<TrainingResult<IndexedTrainingCatalog>> {
  const opened = await openFenixDatabase(name);
  return opened.ok ? { ok: true, value: new IndexedTrainingCatalog(opened.value, faults) } : opened;
}
